import type {Delimiter, Nodes, Text} from 'mdast'
import {visit} from 'unist-util-visit'
import {SKIP} from 'unist-util-visit-parents'

import {isPositionedNode} from './positioned-node'

function overlapsNode(rangeFrom: number, rangeTo: number, nodeFrom: number, nodeTo: number): boolean {
  return (
    (rangeFrom >= nodeFrom && rangeFrom < nodeTo) ||
    (rangeTo > nodeFrom && rangeTo <= nodeTo) ||
    (rangeFrom < nodeFrom && rangeTo > nodeTo)
  )
}

/**
 * Returns the set of all the nodes that overlap the given range, including the root node (`parent`).
 */
export function getNodesOverlappingRange(tree: Nodes, from: number, to: number) {
  const result = new Set<Nodes>()

  visit(tree, node => {
    if (!isPositionedNode(node)) return

    if (overlapsNode(from, to, node.position.start.offset, node.position.end.offset)) result.add(node)
    else return SKIP // if this node doesn't overlap the range, we know its children won't either so we can skip them
  })

  return result
}

/** Check if the node fully contains the range. */
function containsRange(rangeFrom: number, rangeTo: number, nodeFrom: number, nodeTo: number): boolean {
  return rangeFrom >= nodeFrom && rangeTo <= nodeTo && !(rangeFrom === nodeFrom && rangeTo === nodeTo)
}

/**
 * Returns the set of all nodes that fully contain the given range. Inclusive of nodes that start or end at the range
 * boundaries, but not nodes that both start _and_ end at range boundaries:
 *
 * - Included: `|...range|`, `|range...|`, `|...range...|`
 * - Excluded: `range|...|`, `ra|nge..|`, `|range|`
 */
export function getNodesContainingRange(tree: Nodes, from: number, to: number) {
  const result = new Set<Nodes>()

  visit(tree, node => {
    if (!isPositionedNode(node)) return

    if (containsRange(from, to, node.position.start.offset, node.position.end.offset)) result.add(node)
    else return SKIP // If this node does not contain this range, neither can any of its descendants
  })

  return result
}

/** Check if the range fully contains the node. */
function containsNode(rangeFrom: number, rangeTo: number, nodeFrom: number, nodeTo: number): boolean {
  return nodeFrom >= rangeFrom && nodeTo <= rangeTo
}

/**
 * Returns the set of all nodes that are fully inside the given range. Inclusive of start and end.
 */
export function getNodesInsideRange(tree: Nodes, from: number, to: number) {
  const result = new Set<Nodes>()

  visit(tree, node => {
    if (!isPositionedNode(node)) return

    const nodeFrom = node.position.start.offset
    const nodeTo = node.position.end.offset

    // If the range and node don't overlap, none of the descendants of this node can possibly be contained inside the range
    if (!overlapsNode(from, to, nodeFrom, nodeTo)) return SKIP

    if (containsNode(from, to, nodeFrom, nodeTo)) result.add(node)
  })

  return result
}

/** Return the lowest node in the tree at the given `offset` that is not a `text` or `delimiter` node. */
export function getNodeAtOffset(tree: Nodes, offset: number): Exclude<Nodes, Text | Delimiter> | undefined {
  let result: Exclude<Nodes, Text | Delimiter> | undefined = undefined

  visit(tree, node => {
    if (!isPositionedNode(node)) return

    // Since we go from top down, the last one encountered will be the deepest node at this offset
    if (
      containsRange(offset, offset, node.position.start.offset, node.position.end.offset) &&
      node.type !== 'text' &&
      node.type !== 'delimiter'
    )
      result = node
    else return SKIP // If this node does not contain this offset, neither can any of its descendants
  })

  return result
}
